/** @format */

import axios from "axios";
import { useAuthStore } from "../../context/useAuthStore";
import toast from "react-hot-toast";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaQuoteRight,
  FaUndo,
  FaRedo,
  FaLink,
  FaUnlink,
  FaImage,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
} from "react-icons/fa";

interface TextEditorProps {
  value: string;
  onChange: (content: string) => void;
  label?: string;
  className?: string;
  error?: boolean;
  errorMessage?: string;
}

const MenuBar = ({ editor }: { editor: any }) => {
  if (!editor) {
    return null;
  }

  const { token } = useAuthStore();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const toastId = toast.loading("Uploading image...");
      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axios.post(
          `${API_BASE_URL}/imageupload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        if (
          response.data &&
          response.data.success &&
          response.data.data &&
          response.data.data.imageUrl
        ) {
          let imageUrl = response.data.data.imageUrl;
          const filename = imageUrl.split("/").pop();
          const fullImageUrl = `https://www.llb360.com/uploads/blogs/${filename}`;
          editor.chain().focus().setImage({ src: fullImageUrl }).run();
          toast.success("Image uploaded successfully", { id: toastId });
        } else {
          throw new Error("Invalid response structure");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image", { id: toastId });
      }
    }
    // Reset the input
    e.target.value = "";
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleImageChange}
      />

      {/* Headings Dropdown */}
      <select
        className="p-1.5 rounded border border-gray-300 text-sm bg-white hover:bg-gray-100 cursor-pointer focus:outline-none focus:border-myButtonBg"
        onChange={(e) => {
          const level = parseInt(e.target.value);
          if (level === 0) {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: level as any })
              .run();
          }
        }}
        value={
          editor.isActive("heading", { level: 1 })
            ? "1"
            : editor.isActive("heading", { level: 2 })
            ? "2"
            : editor.isActive("heading", { level: 3 })
            ? "3"
            : editor.isActive("heading", { level: 4 })
            ? "4"
            : editor.isActive("heading", { level: 5 })
            ? "5"
            : editor.isActive("heading", { level: 6 })
            ? "6"
            : "0"
        }
      >
        <option value="0">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
      </select>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Basic Formatting */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("bold") ? "bg-gray-300" : ""
        }`}
        title="Bold"
      >
        <FaBold size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("italic") ? "bg-gray-300" : ""
        }`}
        title="Italic"
      >
        <FaItalic size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("underline") ? "bg-gray-300" : ""
        }`}
        title="Underline"
      >
        <FaUnderline size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("strike") ? "bg-gray-300" : ""
        }`}
        title="Strike"
      >
        <FaStrikethrough size={14} />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Alignment */}
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive({ textAlign: "left" }) ? "bg-gray-300" : ""
        }`}
        title="Align Left"
      >
        <FaAlignLeft size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive({ textAlign: "center" }) ? "bg-gray-300" : ""
        }`}
        title="Align Center"
      >
        <FaAlignCenter size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive({ textAlign: "right" }) ? "bg-gray-300" : ""
        }`}
        title="Align Right"
      >
        <FaAlignRight size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive({ textAlign: "justify" }) ? "bg-gray-300" : ""
        }`}
        title="Justify"
      >
        <FaAlignJustify size={14} />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Lists and Blockquote */}
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("bulletList") ? "bg-gray-300" : ""
        }`}
        title="Bullet List"
      >
        <FaListUl size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("orderedList") ? "bg-gray-300" : ""
        }`}
        title="Ordered List"
      >
        <FaListOl size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("blockquote") ? "bg-gray-300" : ""
        }`}
        title="Blockquote"
      >
        <FaQuoteRight size={14} />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Insert */}
      <button
        type="button"
        onClick={setLink}
        className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${
          editor.isActive("link") ? "bg-gray-300" : ""
        }`}
        title="Link"
      >
        <FaLink size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
        className="p-1.5 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
        title="Unlink"
      >
        <FaUnlink size={14} />
      </button>
      <button
        type="button"
        onClick={addImage}
        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
        title="Image"
      >
        <FaImage size={14} />
      </button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* History */}
      <button
        type="button"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
        className="p-1.5 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
        title="Undo"
      >
        <FaUndo size={14} />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
        className="p-1.5 rounded hover:bg-gray-200 transition-colors disabled:opacity-50"
        title="Redo"
      >
        <FaRedo size={14} />
      </button>
    </div>
  );
};

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  label,
  className = "",
  error,
  errorMessage,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-gray-500 hover:underline",
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: "max-w-full h-auto rounded-lg my-4",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose-base focus:outline-none min-h-[300px] p-8 max-w-none text-myLabelText bg-white shadow-sm prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-p:leading-relaxed prose-a:text-gray-600 prose-a:underline hover:prose-a:text-gray-500 overflow-y-auto",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <div className={`mb-4 ${className} text-left`}>
      {label && (
        <label className="textInputLabel mb-2 inline-block">{label}</label>
      )}
      <div
        className={`border rounded-xl bg-gray-100 overflow-hidden transition-colors flex flex-col ${
          error
            ? "border-red-600"
            : "border-myInputBorder focus-within:border-gray-600"
        }`}
      >
        <MenuBar editor={editor} />
        <div className="p-4 bg-gray-100 flex-1 overflow-y-auto max-h-[600px]">
          <section className="blogContent">
            <EditorContent editor={editor} className="mx-auto max-w-3xl" />
          </section>
        </div>
      </div>
      {error && <p className="textInputErrorMessage">{errorMessage}</p>}
    </div>
  );
};

export default TextEditor;
